<template>
  <div class="pbx-liste">
    <Datatable
      ref="datatable"
      route="/pbx/liste"
      :fields="fields"
      :data_filters="filters"
      titre="Liste des appels"
      :sort="sort"
    >
      <!-- ------------------------------------------------------------------
           Filtres : le back n'expose pas de configuration /param/filter pour
           cette route, on dessine donc nos propres champs. Ils ecrivent dans
           `filters`, que Datatable fusionne dans le body au moment de la
           recherche.
      ------------------------------------------------------------------- -->
      <template #action>
        <b-button
          variant="light-success"
          class="btn-icon"
          size="md"
          v-b-tooltip.hover
          :title="exportTitle"
          :disabled="csv.running"
          @click="exportCSV()"
        >
          <i class="fa fa-size" :class="csv.running ? 'fa-spin fa-spinner' : 'fa-file-excel'"></i>
        </b-button>
        <span v-if="csv.running && csv.total > 0" class="ml-2 text-muted small">
          {{ csv.loaded }} / {{ csv.total }}
        </span>
      </template>

      <template #customField>
        <b-row>
          <b-col md="3">
            <b-form-group label="Appelant :">
              <b-form-input v-model="form.caller" placeholder="Numero de l'appelant" trim></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Appele :">
              <b-form-input v-model="form.callee" placeholder="Numero ou extension" trim></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Utilisateur PBX :">
              <treeselect
                v-model="form.users"
                :options="userOptions"
                :multiple="true"
                :clear-on-select="true"
                placeholder="Tous les utilisateurs"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Type d'appel :">
              <treeselect
                v-model="form.types"
                :options="typeOptions"
                :multiple="true"
                :clear-on-select="true"
                placeholder="Tous les types"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Appels depuis le :">
              <b-form-input type="date" v-model="form.min_start"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Appels jusqu'au :">
              <b-form-input type="date" v-model="form.max_start"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Duree minimum (sec.) :">
              <b-form-input type="number" min="0" v-model.number="form.min_duration" placeholder="0"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Duree maximum (sec.) :">
              <b-form-input type="number" min="0" v-model.number="form.max_duration" placeholder="Sans limite"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="justify-content-md-center mb-3">
          <b-col md="12" class="text-center">
            <b-button size="sm" variant="light" @click="resetFilters()">
              <i class="fas fa-eraser mr-1"></i> Effacer les filtres d'appel
            </b-button>
          </b-col>
        </b-row>
      </template>

      <template #cell(type)="data">
        <span class="pbx-type" :class="'pbx-type--' + typeMeta(data.value).slug">
          <i :class="typeMeta(data.value).icon"></i>
          {{ typeMeta(data.value).label }}
        </span>
      </template>

      <template #cell(caller)="data">
        <span class="pbx-num">{{ data.value || '—' }}</span>
      </template>

      <template #cell(callee)="data">
        <span class="pbx-num">{{ data.value || '—' }}</span>
      </template>

      <template #cell(pbx_user)="data">
        <span v-if="userLabel(data.value)">{{ userLabel(data.value) }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(start)="data">
        <span class="pbx-date">{{ $api.moment(data.value).format('DD/MM/YYYY') }}</span>
        <span class="pbx-hour">{{ $api.moment(data.value).format('HH:mm') }}</span>
      </template>

      <template #cell(duration)="data">
        <span class="pbx-num">{{ toDuration(data.value) }}</span>
      </template>

      <!-- ------------------------------------------------------------------
           Lecteur. Rien n'est precharge : la balise <audio> est unique, en
           `preload="none"`, et ne recoit une source qu'au clic sur play. Un
           second appel lance remplace donc la source du premier, ce qui le
           coupe net — impossible d'avoir deux flux en meme temps.
      ------------------------------------------------------------------- -->
      <template #cell(player)="data">
        <div v-if="!data.item.url" class="text-muted small">
          <i class="fas fa-microphone-slash mr-1"></i> aucun enregistrement
        </div>
        <div v-else class="pbx-player" :class="{'pbx-player--active': isCurrent(data.item)}">
          <b-button
            size="sm"
            class="btn-icon pbx-play"
            :variant="isPlaying(data.item) ? 'primary' : 'light-primary'"
            :title="isPlaying(data.item) ? 'Mettre en pause' : 'Ecouter l\'appel'"
            v-b-tooltip.hover
            @click="toggle(data.item)"
          >
            <i class="fas" :class="playIcon(data.item)"></i>
          </b-button>

          <template v-if="isCurrent(data.item)">
            <input
              type="range"
              class="pbx-seek"
              min="0"
              :max="playerDuration(data.item)"
              step="0.1"
              :value="player.currentTime"
              @input="seek($event)"
            />
            <span class="pbx-time">
              {{ toDuration(player.currentTime) }} / {{ toDuration(playerDuration(data.item)) }}
            </span>
            <b-dropdown size="sm" variant="light" right no-caret toggle-class="pbx-rate">
              <template #button-content>x{{ player.rate }}</template>
              <b-dropdown-item
                v-for="r in rates"
                :key="r"
                :active="player.rate === r"
                @click="setRate(r)"
              >x{{ r }}</b-dropdown-item>
            </b-dropdown>
          </template>

          <b-button
            size="sm"
            class="btn-icon"
            variant="light"
            title="Telecharger l'enregistrement"
            v-b-tooltip.hover
            :href="$api.link(data.item.url)"
            target="_blank"
            download
          >
            <i class="fas fa-download"></i>
          </b-button>
        </div>
      </template>
    </Datatable>

    <audio
      ref="audio"
      preload="none"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @play="player.paused = false"
      @pause="player.paused = true"
      @ended="onEnded"
      @error="onError"
    ></audio>
  </div>
</template>
<script>
// `treeselect` est enregistre globalement (src/core/plugins/treeselect.js),
// on ne le reimporte pas ici.
import Datatable from '@/components/Datatable';

// Taille des paquets de l'export, et plafond de securite : 100 x 1000 lignes.
const EXPORT_PAGE_SIZE = 1000;
const EXPORT_MAX_PAGES = 100;

const TYPES = {
  InboundExternal: { label: 'Entrant', slug: 'in', icon: 'fas fa-arrow-down' },
  OutboundExternal: { label: 'Sortant', slug: 'out', icon: 'fas fa-arrow-up' },
  Local: { label: 'Interne', slug: 'local', icon: 'fas fa-exchange-alt' },
};

export default {
  name: 'PbxListe',
  components: {
    Datatable,
  },
  data() {
    return {
      fields: [
        { key: 'type', label: 'Type', sortable: true },
        { key: 'caller', label: 'Appelant', sortable: true },
        { key: 'callee', label: 'Appele', sortable: true },
        { key: 'pbx_user', label: 'Utilisateur', sortable: false },
        { key: 'start', label: 'Debut', sortable: true },
        { key: 'duration', label: 'Duree', sortable: true },
        { key: 'player', label: 'Enregistrement', sortable: false },
      ],
      sort: {
        key: 'start',
        value: 'DESC',
      },
      // Objet transmis a Datatable : c'est lui qui part dans le body.
      filters: {
        caller: null,
        callee: null,
        'pbx_user.id': [],
        type: [],
        min_start: null,
        max_start: null,
        min_duration: null,
        max_duration: null,
      },
      // Etat des champs a l'ecran, converti vers `filters` par le watcher.
      form: {
        caller: null,
        callee: null,
        users: [],
        types: [],
        min_start: null,
        max_start: null,
        min_duration: null,
        max_duration: null,
      },
      // Alimentes par readMeta() a chaque reponse de recherche : vides tant
      // que le premier appel n'a pas repondu.
      userOptions: [],
      typeOptions: [],
      csv: {
        running: false,
        rows: [],
        loaded: 0,
        total: 0,
      },
      rates: [1, 1.25, 1.5, 2],
      player: {
        id: null,
        paused: true,
        currentTime: 0,
        duration: 0,
        rate: 1,
      },
    };
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.syncFilters();
      },
    },
  },
  computed: {
    exportTitle() {
      if (this.csv.running) {
        return 'Export en cours...';
      }
      return 'Exporter tous les appels de la recherche en CSV';
    },
  },
  methods: {
    // ------------------------------------------------------------------
    // Export CSV.
    //
    // Porte sur la recherche entiere, pas sur la page affichee : on rejoue
    // la requete avec les memes filtres et le meme tri, page par page, en
    // paquets de 1000. Un seul appel avec `limit: totalRow` serait plus
    // court a ecrire mais expose a un timeout des que la recherche est
    // large.
    // ------------------------------------------------------------------
    exportCSV() {
      if (this.csv.running) {
        return;
      }
      const dt = this.$refs.datatable;
      if (!dt) {
        return;
      }
      if (dt.datatable.totalRow === 0) {
        this.$bvToast.toast("La recherche ne renvoie aucun appel a exporter", {
          title: 'Export',
          variant: 'warning',
          solid: true,
        });
        return;
      }
      this.csv.running = true;
      this.csv.rows = [];
      this.csv.loaded = 0;
      this.csv.total = dt.datatable.totalRow;
      this.fetchExportPage(1);
    },
    fetchExportPage(page) {
      const dt = this.$refs.datatable;
      const params = { ...dt.params, limit: EXPORT_PAGE_SIZE, page };
      this.$api.ajax('/pbx/liste', params, (res) => {
        if (!res || !Array.isArray(res.data)) {
          this.csv.running = false;
          this.$bvToast.toast("L'export a echoue, la recherche n'a pas repondu", {
            title: 'Export',
            variant: 'danger',
            solid: true,
          });
          return;
        }
        this.csv.rows = this.csv.rows.concat(res.data);
        this.csv.loaded = this.csv.rows.length;
        if (res.totalRow) {
          this.csv.total = res.totalRow;
        }
        // Trois conditions d'arret, dont deux sont des garde-fous : une page
        // incomplete signe la fin, et EXPORT_MAX_PAGES protege d'une
        // pagination qui ne se terminerait jamais.
        const complete = res.data.length === EXPORT_PAGE_SIZE;
        const remaining = this.csv.rows.length < this.csv.total;
        if (complete && remaining && page < EXPORT_MAX_PAGES) {
          this.fetchExportPage(page + 1);
          return;
        }
        if (complete && remaining) {
          this.$bvToast.toast(
            `Export limite aux ${this.csv.rows.length} premiers appels sur ${this.csv.total}`,
            { title: 'Export tronque', variant: 'warning', solid: true }
          );
        }
        this.downloadCSV(this.csv.rows);
        this.csv.running = false;
      });
    },
    downloadCSV(rows) {
      const head = [
        'Type',
        'Appelant',
        'Appele',
        'Utilisateur',
        'Date',
        'Heure',
        'Duree',
        'Duree (sec.)',
        'Enregistrement',
      ];
      const lines = [head.join(';')];
      rows.forEach((r) => {
        const start = r.start ? this.$api.moment(r.start) : null;
        lines.push(
          [
            this.typeMeta(r.type).label,
            r.caller,
            r.callee,
            this.userLabel(r.pbx_user),
            start ? start.format('DD/MM/YYYY') : '',
            start ? start.format('HH:mm:ss') : '',
            this.toDuration(r.duration),
            r.duration,
            r.url ? this.$api.link(r.url) : '',
          ]
            .map((v) => this.csvCell(v))
            .join(';')
        );
      });
      // BOM UTF-8, sans quoi Excel massacre les accents. Blob plutot qu'une
      // URL `data:` : celle-ci plafonne autour de quelques Mo selon les
      // navigateurs, ce qu'un export multi-pages depasse vite.
      const blob = new Blob(['﻿' + lines.join('\r\n')], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `appels_${this.$api.moment().format('YYYYMMDD_HHmm')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      this.$bvToast.toast(`${rows.length} appels exportes`, {
        title: 'Export termine',
        variant: 'success',
        solid: true,
      });
    },
    csvCell(value) {
      if (value === null || value === undefined) {
        return '';
      }
      const text = String(value);
      if (text.search(/[";\r\n]/) === -1) {
        return text;
      }
      return `"${text.replace(/"/g, '""')}"`;
    },
    // ------------------------------------------------------------------
    // Options des filtres Utilisateur / Type.
    //
    // Elles vivent dans `meta`, cote reponse de recherche. On ne peut pas
    // les exposer par une computed lisant `$refs` : les refs ne sont pas
    // reactives en Vue 2, la computed serait evaluee une fois — refs encore
    // vides — et plus jamais rafraichie. D'ou ce watcher pose dans
    // mounted() sur `datatable.datatable`, qui est bien reactif, lui.
    // ------------------------------------------------------------------
    watchMeta() {
      const dt = this.$refs.datatable;
      if (!dt) {
        return;
      }
      this.$watch(
        () => dt.datatable,
        (res) => this.readMeta(res),
        { immediate: true }
      );
    },
    readMeta(res) {
      if (!res) {
        return;
      }
      const meta = res.meta || {};
      // L'API renvoie parfois RecordUser vide : on retombe alors sur les
      // utilisateurs presents dans les lignes affichees, faute de mieux. On
      // les cumule au lieu de les remplacer, sinon filtrer sur un utilisateur
      // reduirait la liste a ce seul utilisateur — impossible d'en changer.
      const users = this.normalizeUsers(meta.RecordUser);
      this.userOptions =
        users.length > 0
          ? users
          : this.dedupe(this.userOptions.concat(this.usersFromRows(res.data)));
      const types =
        Array.isArray(meta.RecordType) && meta.RecordType.length > 0
          ? meta.RecordType
          : Object.keys(TYPES);
      this.typeOptions = types.map((t) => ({
        id: t,
        label: this.typeMeta(t).label,
      }));
    },
    // Tolerant sur la forme recue : liste d'objets, liste de valeurs, ou
    // dictionnaire id => libelle.
    normalizeUsers(list) {
      if (!list) {
        return [];
      }
      let entries = [];
      if (Array.isArray(list)) {
        entries = list.map((u) => {
          if (u === null || u === undefined) {
            return null;
          }
          if (typeof u === 'object') {
            return { id: String(u.id), label: u.title || u.titre || String(u.id) };
          }
          return { id: String(u), label: String(u) };
        });
      } else if (typeof list === 'object') {
        entries = Object.keys(list).map((k) => ({ id: String(k), label: String(list[k]) }));
      }
      return this.dedupe(entries.filter((e) => e !== null && e.id !== 'undefined'));
    },
    usersFromRows(rows) {
      if (!Array.isArray(rows)) {
        return [];
      }
      const flat = [];
      rows.forEach((row) => {
        (row.pbx_user || []).forEach((u) => {
          flat.push({ id: String(u.id), label: u.title || u.titre || String(u.id) });
        });
      });
      return this.dedupe(flat);
    },
    dedupe(entries) {
      const seen = {};
      const out = [];
      entries.forEach((e) => {
        if (seen[e.id] === undefined) {
          seen[e.id] = true;
          out.push(e);
        }
      });
      return out;
    },
    syncFilters() {
      this.filters.caller = this.blank(this.form.caller);
      this.filters.callee = this.blank(this.form.callee);
      this.filters['pbx_user.id'] = this.form.users || [];
      this.filters.type = this.form.types || [];
      this.filters.min_start = this.toIso(this.form.min_start, false);
      this.filters.max_start = this.toIso(this.form.max_start, true);
      this.filters.min_duration = this.blank(this.form.min_duration);
      this.filters.max_duration = this.blank(this.form.max_duration);
    },
    blank(value) {
      if (value === '' || value === undefined) {
        return null;
      }
      return value;
    },
    // `date` borne la journee entiere : debut de journee pour min_start, fin
    // de journee pour max_start, sinon un appel de 14h serait exclu d'un
    // max_start pose sur le meme jour.
    toIso(date, endOfDay) {
      if (!date) {
        return null;
      }
      const m = this.$api.moment(date);
      if (!m.isValid()) {
        return null;
      }
      return (endOfDay ? m.endOf('day') : m.startOf('day')).toISOString();
    },
    resetFilters() {
      this.form = {
        caller: null,
        callee: null,
        users: [],
        types: [],
        min_start: null,
        max_start: null,
        min_duration: null,
        max_duration: null,
      };
      // Le watcher sur `form` est asynchrone : on repercute nous-memes avant
      // de relancer, sinon la recherche partirait avec les anciens filtres.
      this.syncFilters();
      const dt = this.$refs.datatable;
      if (dt) {
        // Sans retour page 1, on resterait sur une page qui n'existe plus une
        // fois les filtres tombes.
        dt.datatable.currentPage = 1;
        dt.datatableInit();
      }
    },
    typeMeta(type) {
      return TYPES[type] || { label: type || '—', slug: 'other', icon: 'fas fa-phone' };
    },
    userLabel(users) {
      if (!Array.isArray(users) || users.length === 0) {
        return '';
      }
      return users.map((u) => u.title || u.titre || u.id).join(', ');
    },
    toDuration(seconds) {
      const total = Math.max(0, Math.floor(seconds || 0));
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (h > 0) {
        return `${h}:${pad(m)}:${pad(s)}`;
      }
      return `${pad(m)}:${pad(s)}`;
    },
    isCurrent(item) {
      return this.player.id === item.id;
    },
    isPlaying(item) {
      return this.isCurrent(item) && this.player.paused === false;
    },
    playIcon(item) {
      return this.isPlaying(item) ? 'fa-pause' : 'fa-play';
    },
    // La duree annoncee par l'API sert de repli tant que le fichier n'a pas
    // livre ses metadonnees, pour que la barre ne demarre pas a zero.
    playerDuration(item) {
      if (this.player.duration > 0) {
        return this.player.duration;
      }
      return item.duration || 0;
    },
    toggle(item) {
      const audio = this.$refs.audio;
      if (!audio || !item.url) {
        return;
      }
      if (this.isCurrent(item)) {
        if (audio.paused) {
          audio.play();
        } else {
          audio.pause();
        }
        return;
      }
      // Changement de ligne : on repart du debut, l'ecoute precedente est
      // abandonnee.
      audio.pause();
      this.player.id = item.id;
      this.player.currentTime = 0;
      this.player.duration = 0;
      audio.src = this.$api.link(item.url);
      audio.load();
      audio.playbackRate = this.player.rate;
      audio.play();
    },
    seek(event) {
      const audio = this.$refs.audio;
      if (!audio) {
        return;
      }
      const time = Number(event.target.value);
      audio.currentTime = time;
      this.player.currentTime = time;
    },
    setRate(rate) {
      this.player.rate = rate;
      const audio = this.$refs.audio;
      if (audio) {
        audio.playbackRate = rate;
      }
    },
    onTimeUpdate() {
      this.player.currentTime = this.$refs.audio.currentTime;
    },
    onLoadedMetadata() {
      const d = this.$refs.audio.duration;
      this.player.duration = isFinite(d) ? d : 0;
      this.$refs.audio.playbackRate = this.player.rate;
    },
    onEnded() {
      this.player.paused = true;
      this.player.currentTime = 0;
    },
    onError() {
      if (this.player.id === null) {
        return;
      }
      this.player.id = null;
      this.player.paused = true;
      this.$bvToast.toast("L'enregistrement n'a pas pu etre lu", {
        title: 'Lecture impossible',
        variant: 'danger',
        solid: true,
      });
    },
  },
  mounted() {
    this.watchMeta();
  },
  beforeDestroy() {
    const audio = this.$refs.audio;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
    }
  },
};
</script>
<style scoped>
.pbx-liste >>> table td {
  vertical-align: middle;
}
.pbx-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.pbx-date {
  display: block;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.pbx-hour {
  display: block;
  font-size: 0.85rem;
  color: #b5b5c3;
  font-variant-numeric: tabular-nums;
}
.pbx-type {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
}
.pbx-type i {
  font-size: 0.7rem;
}
.pbx-type--in {
  background: #e8fff3;
  color: #1bc5bd;
}
.pbx-type--out {
  background: #e1f0ff;
  color: #3699ff;
}
.pbx-type--local {
  background: #f3f6f9;
  color: #7e8299;
}
.pbx-type--other {
  background: #fff4de;
  color: #ffa800;
}
.pbx-player {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}
.pbx-player--active {
  background: #f3f6f9;
}
.pbx-play {
  flex: 0 0 auto;
}
.pbx-seek {
  flex: 1 1 140px;
  min-width: 120px;
  height: 4px;
  cursor: pointer;
  accent-color: #3699ff;
}
.pbx-time {
  font-size: 0.8rem;
  color: #7e8299;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.pbx-liste >>> .pbx-rate {
  font-variant-numeric: tabular-nums;
  min-width: 3.2rem;
}
</style>
